import { RedoCheckboxCard } from "@redotech/arbiter/checkbox/redo-checkbox-card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { RedoItemFulfillment } from "@redotech/redo-fulfillment-common/redo-item-fulfillment";
import AlertTriangle from "@redotech/redo-icons/alert-triangle.svg";
import Subscription from "@redotech/redo-icons/currency-dollar-circle.svg";
import CoverageIcon from "@redotech/redo-icons/package.svg";
import { CoverageBillingTrigger } from "@redotech/redo-return-coverage-common/coverage-billing-trigger";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput, LabelSize } from "@redotech/redo-web/labeled-input";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { booleanEqual, stringEqual } from "@redotech/util/equal";
import { memo } from "react";

export const billingTypeForm = groupInput({
  subscriptionEnabled: input<boolean>({ equal: booleanEqual }),
  coverageBillingTrigger: input<CoverageBillingTrigger>({ equal: stringEqual }),
  redoItemFulfillment: input<RedoItemFulfillment>({ equal: stringEqual }),
});

export type BillingTypeForm = InputSchema.Input<typeof billingTypeForm>;

export type BillingTypeFormValue = InputSchema.Value<typeof billingTypeForm>;
export const billingTypeDefault: BillingTypeFormValue = {
  subscriptionEnabled: false,
  coverageBillingTrigger: CoverageBillingTrigger.ORDER_CREATION,
  redoItemFulfillment: RedoItemFulfillment.PARTIAL,
};

const COVERAGE_BILLING_TRIGGER_OPTIONS = [
  {
    id: CoverageBillingTrigger.ORDER_CREATION,
    type: "text" as const,
    text: "Order creation",
    value: CoverageBillingTrigger.ORDER_CREATION,
  },
  {
    id: CoverageBillingTrigger.REDO_PRODUCT_FULFILLMENT,
    type: "text" as const,
    text: "Redo product fulfillment",
    value: CoverageBillingTrigger.REDO_PRODUCT_FULFILLMENT,
  },
];

const REDO_ITEM_FULFILLMENT_OPTIONS = [
  {
    id: RedoItemFulfillment.NONE,
    type: "text" as const,
    text: "None (manual)",
    value: RedoItemFulfillment.NONE,
  },
  {
    id: RedoItemFulfillment.PARTIAL,
    type: "text" as const,
    text: "Partial",
    value: RedoItemFulfillment.PARTIAL,
  },
  {
    id: RedoItemFulfillment.IMMEDIATE,
    type: "text" as const,
    text: "Immediate",
    value: RedoItemFulfillment.IMMEDIATE,
  },
];

export const BillingTypeSettings = memo(function BillingTypeSettings({
  billingType,
}: {
  billingType: BillingTypeForm;
}) {
  const { subscriptionEnabled } = billingType.inputs;

  return (
    <Flex align="stretch" data-target="billing-type" dir="column">
      <Flex align="stretch" dir="column">
        <RedoCheckboxCard
          checked={!subscriptionEnabled.value}
          icon={<CoverageIcon />}
          inputType="radio"
          onClick={function (): void {
            if (subscriptionEnabled.value) {
              subscriptionEnabled.setValue(false);
            }
          }}
          title="Coverage"
        >
          Either the customer purchases return coverage, or merchant works cost
          into shipping.
        </RedoCheckboxCard>
        <RedoCheckboxCard
          checked={subscriptionEnabled.value}
          icon={<Subscription />}
          inputType="radio"
          onClick={function (): void {
            if (!subscriptionEnabled.value) {
              subscriptionEnabled.setValue(true);
            }
          }}
          title="Subscription"
        >
          Merchant pays a fee for each return, but return coverage is not
          purchased by customer.
        </RedoCheckboxCard>
      </Flex>
    </Flex>
  );
});

export const CoverageBillingSettings = memo(function CoverageBillingSettings({
  billingType,
}: {
  billingType: BillingTypeForm;
}) {
  const { coverageBillingTrigger, redoItemFulfillment } = billingType.inputs;

  const isRedoFulfillmentTrigger =
    coverageBillingTrigger.value ===
    CoverageBillingTrigger.REDO_PRODUCT_FULFILLMENT;

  const getWarningMessage = (): string | null => {
    if (!isRedoFulfillmentTrigger) {
      return null;
    }
    if (redoItemFulfillment.value === RedoItemFulfillment.NONE) {
      return 'Warning: "Redo Item Fulfillment" is set to "None" (manual). Billing will not occur until the Redo item is manually fulfilled, which could be forgotten. Consider using "Partial" fulfillment if you want billing to trigger when the order is fulfilled.';
    }
    if (redoItemFulfillment.value === RedoItemFulfillment.IMMEDIATE) {
      return 'Note: "Redo Item Fulfillment" is set to "Immediate", so billing will occur immediately after order creation. This is effectively the same as "Order creation" trigger. Consider using "Partial" fulfillment if you want billing to trigger when the order is fulfilled.';
    }
    return null;
  };

  const warningMessage = getWarningMessage();

  return (
    <Flex dir="column" gap="3xl">
      <LabeledInput
        description="Determines when billing for Redo coverage is triggered."
        label="Coverage billing trigger"
        size={LabelSize.SMALL}
      >
        <RedoSingleSelectDropdownInput<CoverageBillingTrigger>
          options={COVERAGE_BILLING_TRIGGER_OPTIONS}
          optionSelected={({ item }) => {
            coverageBillingTrigger.setValue(item.value);
          }}
          selectedItem={COVERAGE_BILLING_TRIGGER_OPTIONS.find(
            (o) => o.value === coverageBillingTrigger.value,
          )}
        />
      </LabeledInput>
      <LabeledInput
        description="Determines when the Redo line item is automatically fulfilled."
        label="Redo item fulfillment"
        size={LabelSize.SMALL}
      >
        <RedoSingleSelectDropdownInput<RedoItemFulfillment>
          options={REDO_ITEM_FULFILLMENT_OPTIONS}
          optionSelected={({ item }) => {
            redoItemFulfillment.setValue(item.value);
          }}
          selectedItem={REDO_ITEM_FULFILLMENT_OPTIONS.find(
            (o) => o.value === redoItemFulfillment.value,
          )}
        />
      </LabeledInput>
      {warningMessage && (
        <div className={gridCss.span12}>
          <Flex
            align="center"
            gap="sm"
            p="md"
            style={{
              backgroundColor:
                "var(--redo-colors-background-bg-warning-primary)",
              borderRadius: "var(--border-radius-lg)",
              border: "1px solid var(--redo-border-warning-light-color)",
            }}
          >
            <AlertTriangle
              style={{
                color: "var(--redo-colors-foreground-fg-warning-secondary)",
                flexShrink: 0,
                width: 16,
                height: 16,
              }}
            />
            <Text
              fontSize="xs"
              style={{ color: "var(--redo-colors-text-text-warning-primary)" }}
            >
              {warningMessage}
            </Text>
          </Flex>
        </div>
      )}
    </Flex>
  );
});
